// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { InvoiceDetail } from '@/billing/api/types';
import { useDeleteDraftInvoice } from '@/billing/hooks/use-delete-draft-invoice';
import { Warning as WarningIcon } from '@/constants/hardfin-standard-icons';
import { formatCurrencyValue } from '@/currency/utilities/format';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

interface Props {
  isOpen: boolean;
  invoice: InvoiceDetail;
  onClose: () => void;
  onSuccess?: () => void;
  onError?: (
    message: string,
    apiStatusCode: number,
    httpStatusCode: number
  ) => void;
}

function DetailRow(detailProps: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="baseline">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 72 }}
      >
        {detailProps.label}
      </Typography>
      <Typography variant="body2">{detailProps.value}</Typography>
    </Stack>
  );
}

export function DeleteDraftInvoiceDialog(props: Props) {
  const close = () => {
    if (deleteMutation.isPending) {
      return;
    }
    props.onClose();
  };

  const deleteMutation = useDeleteDraftInvoice({
    invoiceKey: props.invoice.key,
    onSuccess: () => {
      props.onClose();
      props.onSuccess?.();
    },
    onError: (message, apiStatusCode, httpStatusCode) => {
      // 404 (already deleted) or a known status conflict (invoice has
      // transitioned out of DRAFT) closes the dialog. Retrying won't
      // help — the parent navigates away on 404 and shows a known-error
      // snackbar on status conflicts.
      const isKnownStatusConflict =
        apiStatusCode === ERROR_CODE_MAPPING.invoice_not_deletable;
      if (httpStatusCode === 404 || isKnownStatusConflict) {
        props.onClose();
        props.onError?.(message, apiStatusCode, httpStatusCode);
        return;
      }
      // Unexpected errors (5xx / unknown) keep the dialog open — the
      // user's intent to delete is unchanged and a retry may succeed.
      props.onError?.(message, apiStatusCode, httpStatusCode);
    },
  });

  const handleConfirm = () => {
    deleteMutation.mutate();
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={props.isOpen} onClose={close}>
      <DialogTitle
        sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}
      >
        Delete draft
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Alert
          severity="warning"
          icon={<WarningIcon fontSize="medium" />}
          sx={{ marginY: 2, alignItems: 'center' }}
        >
          <AlertTitle variant="h6">
            Delete draft invoice <strong>{props.invoice.invoiceNumber}</strong>?
          </AlertTitle>
          <Typography variant="body1">
            All line items will be removed. The underlying charges will return
            to billable status
          </Typography>
        </Alert>

        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack spacing={0.75}>
            <DetailRow label="Invoice" value={props.invoice.invoiceNumber} />
            {props.invoice.customerName && (
              <DetailRow label="Customer" value={props.invoice.customerName} />
            )}
            <DetailRow
              label="Total"
              value={formatCurrencyValue(props.invoice.total)}
            />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Button onClick={close} disabled={deleteMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={deleteMutation.isPending}
        >
          Delete draft
        </Button>
      </DialogActions>
    </Dialog>
  );
}
